import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMicrophone
  } from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import AILightComponent from '../../components/AILightComponent';
import SpeechInputComponent from '../../components/SpeechInputComponent';
import { GenerateRCACode } from '../../components/GenerateThink5whysCode';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

// MULTILINGUAL SUPPORT
import HAFHRCATranslations from './HAFHRCATranslations';

const languages = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी' },
  { code: 'mr', label: 'मराठी' },
  { code: 'gu', label: 'ગુજરાતી' },
  { code: 'bn', label: 'বাংলা' },
  { code: 'ta', label: 'தமிழ்' },
  { code: 'te', label: 'తెలుగు' },
  { code: 'kn', label: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'മലയാളം' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ' },
  { code: 'as', label: 'অসমীয়া' },
];

export default function HAFHRCAHome() {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [fetchData, { loading }] = useFetch();
  const user = authState?.user || {};
  const userId = authState?.user._id;
  const userCode = user.think5whyscode;
  const userName = user.name;
  const [rcaCode, setRcaCode] = useState(GenerateRCACode());
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const t = (key) => HAFHRCATranslations[selectedLanguage]?.[key] || key;
  const rcaTitleRef = useRef(null);
  const listSectionRef = useRef(null);
  const createSectionRef = useRef(null);
  const reportsSectionRef = useRef(null);
  const [activeTab, setActiveTab] = useState('List');
  const [filter, setFilter] = useState("ALL");
  const [dateFilter, setDateFilter] = useState("ALL");
  const [actionFilter, setActionFilter] = useState("ALL");
  const [message, setMessage] = useState({ type: "", text: "" });
  const showMessage = (text, type = "success") => setMessage({ text, type });
  const [activeAssistantId, setActiveAssistantId] = useState(null);
  const [rcaList, setRCAList] = useState([]);
  const [activeAssistantRCAId, setActiveAssistantRCAId] = useState(null);
  const [activeAssistantCreateRCA, setActiveAssistantCreateRCA] = useState(null);
  const [editingRCAId, setEditingRCAId] = useState(null);
  const [expandedRCA, setExpandedRCA] = useState(null);
  const [activeTtsId, setActiveTtsId] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [isListSpeaking, setIsListSpeaking] = useState(false);
  const [showMicrophoneAlert, setShowMicrophoneAlert] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showQuickSolutions, setShowQuickSolutions] = useState(false);
  const [showMoreInputs, setShowMoreInputs] = useState(false);
  const [showInputWhys, setShowInputWhys] = useState(false);

  const [formErrors, setFormErrors] = useState({});
  const isSameDay = (d1, d2) => new Date(d1).toDateString() === new Date(d2).toDateString();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const getWeekRange = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diffToMonday = d.getDate() - day + (day === 0 ? -6 : 1);
    const start = new Date(d.setDate(diffToMonday));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start, end };
  };

  const getMonthRange = (date, offset = 0) => {
    const d = new Date(date);
    const start = new Date(d.getFullYear(), d.getMonth() + offset, 1);
    const end = new Date(d.getFullYear(), d.getMonth() + offset + 1, 0);
    return { start, end };
  };

  const [rcaForm, setRCAForm] = useState({
    user: user?._id,
    userCode: user?.userCode,
    userRCAID: "",
    rcaCode: rcaCode,
    rcaDate: new Date().toISOString().split("T")[0],
    rcaGroupCode: "",
    rcaType: "",
    category: "",
    subCategory: "",
    concernArea: "",
    rcaFor: "Self",
    rcaBy: "Human",
    problemStatement: "",
    why1: "",
    why2: "",
    why3: "",
    why4: "",
    why5: "",
    rootCause: "",
    summary: "",
    remarks: "",
    tags: [],
    quickSolutionsNote: "",
    quickSolutionsSelectionNote: "",
    quickSolutionsActionsNote: "",
    quickImpactNotes: "",
    teamCode: "",
    taskCode: "",
    linkedRCACode: "",
    linkedTaskCode: "",
    rcaAssistantCode: "",
    rcaSupervisorCode: "",
    rcaFromDate: new Date().toISOString().split("T")[0],
    rcaToDate: new Date().toISOString().split("T")[0],
    completionDate: new Date().toISOString().split("T")[0],
    // isCompleted: false,
    isRecurring: false,
    isPriority: false,
    isFinal: false,
    isActive: true,
  });

  const handleRCAChange = (field, value) => {
    setRCAForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // FILTER RCA LIST
  const getFilteredRCA = () => {
    const today = startOfDay(new Date());
    return rcaList.filter((rca) => {
      const dateValue = rca?.date;
      if (!dateValue) { return dateFilter === "all";}
      const d = startOfDay(new Date(dateValue));
      const diffDays = (d - today) / (1000 * 60 * 60 * 24);
      if (dateFilter === "today") { return diffDays === 0; }
      if (dateFilter === "yesterday") { return diffDays === -1;}
      if (dateFilter === "tomorrow") {return diffDays === 1;}
      if (dateFilter === "thisweek") {
        const { start, end } = getWeekRange(new Date());
        return d >= start && d <= end;
      }
      if (dateFilter === "lastweek") {
        const { start, end } = getWeekRange(new Date());
        start.setDate(start.getDate() - 7);
        end.setDate(end.getDate() - 7);
        return d >= start && d <= end;
      }
      if (dateFilter === "nextweek") {
        const { start, end } = getWeekRange(new Date());
        start.setDate(start.getDate() + 7);
        end.setDate(end.getDate() + 7);
        return d >= start && d <= end;
      }
         if (dateFilter === "thismonth") {
        const { start, end } = getMonthRange(new Date());
        return d >= start && d <= end;
      }
      if (dateFilter === "lastmonth") {
        const { start, end } = getMonthRange(new Date(), -1);
        return d >= start && d <= end;
      }
      if (dateFilter === "nextmonth") {
        const { start, end } = getMonthRange(new Date(), 1);
        return d >= start && d <= end;
      }
      // if (dateFilter === "completed") {return rca?.isCompleted === true;}
      // if (dateFilter === "pending") {return rca?.isCompleted === false;}
      if (dateFilter === "priority") {return rca?.isPriority === true;}
      if (dateFilter === "recurring") {return rca?.isRecurring === true;}
      if (dateFilter === "final") {return rca?.isFinal === true;}
      if (dateFilter === "active") {return rca?.isActive === true;}
      return true;
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      const sectionMap = {
        [t('list')]: listSectionRef,
        [t('create')]: createSectionRef,
        [t('reports')]: reportsSectionRef,
      };
      const selectedRef = sectionMap[tab];
      selectedRef?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const tabs = [
    t('list'),
    t('create'),
    t('reports'),
  ];

  const speakText = (data) => {
    if (!window.speechSynthesis) {
      showMessage("Text to Speech is not supported in this browser", "error");
      return;
    }
    speechSynthesis.cancel();
    let text = "";

    // CASE 1: array of rcas
    if (Array.isArray(data)) {
        text = data
        .map(t => `Title: ${t.title || ""}. Category: ${t.category || ""}. Description: ${t.desc || ""}.`) .join(" ");
    }

    // CASE 2: string list (your current button)
    else if (typeof data === "string") {
      text = data;
    }

    // CASE 3: single rca object
    else if (typeof data === "object" && data !== null) {
      text = `Title: ${data.title || ""}. Category: ${data.category || ""}. Description: ${data.desc || ""}.`;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = selectedLanguage === "hi" ? "hi-IN" : "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setActiveTtsId(null);
    };

    utterance.onerror = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setActiveTtsId(null);
      showMessage("Error occurred while speaking", "error");
    };

    speechSynthesis.speak(utterance);
  };

  const pauseSpeech = () => { speechSynthesis.pause(); setIsPaused(true); };
  const resumeSpeech = () => { speechSynthesis.resume(); setIsPaused(false); };
  const stopSpeech = () => {
    speechSynthesis.cancel();
    setIsSpeaking(false);
    setIsPaused(false);
    setActiveTtsId(null);
  };

  // FETCH RCA DATA
  const fetchRCA = async () => {
    try {
      const config = {
        url: "/hafhrca/myrcas",
        method: "post",
        data: { userId: userId },
        headers: { Authorization: authState.token },
      };

      const res = await fetchData(config, { silent: true });

      if (res?.status) {
        const formatted = (res?.records || []).map((a) => ({
          // USER & RCA IDENTITY
          _id: a?._id,
          user: a?.user || "",
          userCode: a?.userCode || "",
          userRCAID: a?.userRCAID || "",
          rcaDate: a?.rcaDate ? a.rcaDate.split("T")[0] : "",
          rcaCode: a?.rcaCode || "",
          rcaGroupCode: a?.rcaGroupCode || "",
          rcaType: a?.rcaType || "",

          // RCA CLASSIFICATION
          category: a?.category || "",
          subCategory: a?.subCategory || "",
          concernArea: a?.concernArea || "",
          rcaFor: a?.rcaFor || "Self",
          rcaBy: a?.rcaBy || "Human",

          // PROBLEM ANALYSIS
          problemStatement: a?.problemStatement || "",
          why1: a?.why1 || "",
          why2: a?.why2 || "",
          why3: a?.why3 || "",
          why4: a?.why4 || "",
          why5: a?.why5 || "",
          rootCause: a?.rootCause || "",
          summary: a?.summary || "",
          remarks: a?.remarks || "",
          tags: a?.tags || [],

          // QUICK SOLUTIONS & IMPACT
          quickSolutionsNote: a?.quickSolutionsNote || "",
          quickSolutionsSelectionNote: a?.quickSolutionsSelectionNote || "",
          quickSolutionsActionsNote: a?.quickSolutionsActionsNote || "",
          quickImpactNotes: a?.quickImpactNotes || "",

          // LINKING & RELATIONS
          teamCode: a?.teamCode || "",
          taskCode: a?.taskCode || "",
          linkedRCACode: a?.linkedRCACode || "",
          linkedTaskCode: a?.linkedTaskCode || "",

          // RCA OWNERSHIP & SUPERVISION
          rcaAssistantCode: a?.rcaAssistantCode || "",
          rcaSupervisorCode: a?.rcaSupervisorCode || "",

          // RCA DURATION
          rcaFromDate: a?.rcaFromDate ? a.rcaFromDate.split("T")[0] : new Date().toISOString().split("T")[0],
          rcaToDate: a?.rcaToDate ? a.rcaToDate.split("T")[0] : new Date().toISOString().split("T")[0],
          completionDate: a?.completionDate ? a.completionDate.split("T")[0] : new Date().toISOString().split("T")[0],

          // RCA STATUS FLAGS
          // isCompleted: a?.isCompleted || false,
          isRecurring: a?.isRecurring || false,
          isPriority: a?.isPriority || false,
          isFinal: a?.isFinal || false,
          isActive: a?.isActive ?? true,

          // TIMESTAMPS
          createdAt: a?.createdAt || "",
          updatedAt: a?.updatedAt || "",
        }));

        setRCAList(formatted);
      }
    } catch (err) {
      console.error(err);
      showMessage("Failed to load RCA records", "error");
    }
  };

  useEffect(() => {
    if (userId) {
      fetchRCA();
    }
  }, [userId]);

  const handleCancelRCA = () => {
    resetRCAForm();

    setIsEditing(false);
    setEditingRCAId(null);
  };

const resetRCAForm = () => {
  setRCAForm({
    user: user?._id || "",
    userCode: user?.userCode || "TU243BM5850150",
    userRCAID: "",
    rcaCode: rcaCode,
    rcaDate: new Date().toISOString().split("T")[0],
    rcaGroupCode: "",
    rcaType: "",
    category: "",
    subCategory: "",
    concernArea: "",
    rcaFor: "Self",
    rcaBy: "Human",
    problemStatement: "",
    why1: "",
    why2: "",
    why3: "",
    why4: "",
    why5: "",
    rootCause: "",
    summary: "",
    remarks: "",
    tags: [],
    quickSolutionsNote: "",
    quickSolutionsSelectionNote: "",
    quickSolutionsActionsNote: "",
    quickImpactNotes: "",
    teamCode: "",
    taskCode: "",
    linkedRCACode: "",
    linkedTaskCode: "",
    rcaAssistantCode: "",
    rcaSupervisorCode: "",
    rcaFromDate:new Date().toISOString().split("T")[0],
    rcaToDate: new Date().toISOString().split("T")[0],
    completionDate: new Date().toISOString().split("T")[0],
    // isCompleted: false,
    isRecurring: false,
    isPriority: false,
    isFinal: false,
    isActive: true,
  });
};

const handleEditRCA = (a) => {
  setIsEditing(true);
  setEditingRCAId(a?._id);

  setRCAForm({
    // USER & RCA IDENTITY
    user: a?.user || "",
    userCode: a?.userCode || "",
    userRCAID: a?.userRCAID || "",
    rcaCode: a?.rcaCode || "",
    rcaDate: a?.rcaDate || new Date().toISOString().split("T")[0],
    rcaGroupCode: a?.rcaGroupCode || "",
    rcaType: a?.rcaType || "",

    // RCA CLASSIFICATION
    category: a?.category || "",
    subCategory: a?.subCategory || "",
    concernArea: a?.concernArea || "",
    rcaFor: a?.rcaFor || "Self",
    rcaBy: a?.rcaBy || "Human",

    // PROBLEM ANALYSIS
    problemStatement: a?.problemStatement || "",
    why1: a?.why1 || "",
    why2: a?.why2 || "",
    why3: a?.why3 || "",
    why4: a?.why4 || "",
    why5: a?.why5 || "",
    rootCause: a?.rootCause || "",
    summary: a?.summary || "",
    remarks: a?.remarks || "",
    tags: a?.tags || [],

    // QUICK SOLUTIONS & IMPACT
    quickSolutionsNote: a?.quickSolutionsNote || "",
    quickSolutionsSelectionNote: a?.quickSolutionsSelectionNote || "",
    quickSolutionsActionsNote: a?.quickSolutionsActionsNote || "",
    quickImpactNotes: a?.quickImpactNotes || "",

    // LINKING & RELATIONS
    teamCode: a?.teamCode || "",
    taskCode: a?.taskCode || "",
    linkedRCACode: a?.linkedRCACode || "",
    linkedTaskCode: a?.linkedTaskCode || "",

    // RCA OWNERSHIP & SUPERVISION
    rcaAssistantCode: a?.rcaAssistantCode || "",
    rcaSupervisorCode: a?.rcaSupervisorCode || "",

    // RCA DURATION
    rcaFromDate: a?.rcaFromDate || new Date().toISOString().split("T")[0],
    rcaToDate: a?.rcaToDate || new Date().toISOString().split("T")[0],
    completionDate: a?.completionDate || new Date().toISOString().split("T")[0],

    // RCA STATUS FLAGS
    // isCompleted: a?.isCompleted || false,
    isRecurring: a?.isRecurring || false,
    isPriority: a?.isPriority || false,
    isFinal: a?.isFinal || false,
    isActive: a?.isActive ?? true,
  });

  setTimeout(() => {
    createSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, 100);
};

const handleSaveRCA = async () => {
  // VALIDATION
  if (!rcaForm?.problemStatement) {
    return showMessage("Problem Statement is required", "error");
  }
  setIsSaving(true);

  try {
    // PAYLOAD
    const payload = {
      // USER & RCA IDENTITY
      user: userId,
      userCode: rcaForm?.userCode || "",
      userRCAID: rcaForm?.userRCAID || "",
      rcaCode: rcaForm?.rcaCode || "",
      rcaDate: rcaForm?.rcaDate || new Date().toISOString().split("T")[0],
      rcaGroupCode: rcaForm?.rcaGroupCode || "",
      rcaType: rcaForm?.rcaType || "",

      // RCA CLASSIFICATION
      category: rcaForm?.category?.trim(),
      subCategory: rcaForm?.subCategory?.trim(),
      concernArea: rcaForm?.concernArea?.trim(),
      rcaFor: rcaForm?.rcaFor || "Self",
      rcaBy: rcaForm?.rcaBy || "Human",

      // PROBLEM ANALYSIS
      problemStatement: rcaForm?.problemStatement?.trim(),
      why1: rcaForm?.why1?.trim(),
      why2: rcaForm?.why2?.trim(),
      why3: rcaForm?.why3?.trim(),
      why4: rcaForm?.why4?.trim(),
      why5: rcaForm?.why5?.trim(),
      rootCause: rcaForm?.rootCause?.trim(),
      summary: rcaForm?.summary?.trim(),
      remarks: rcaForm?.remarks?.trim(),
      tags: Array.isArray(rcaForm?.tags)
        ? rcaForm.tags
        : String(rcaForm?.tags || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),

      // QUICK SOLUTIONS & IMPACT
      quickSolutionsNote: rcaForm?.quickSolutionsNote?.trim(),
      quickSolutionsSelectionNote: rcaForm?.quickSolutionsSelectionNote?.trim(),
      quickSolutionsActionsNote: rcaForm?.quickSolutionsActionsNote?.trim(),
      quickImpactNotes: rcaForm?.quickImpactNotes?.trim(),

      // LINKING & RELATIONS
      teamCode: rcaForm?.teamCode?.trim(),
      taskCode: rcaForm?.taskCode?.trim(),
      linkedRCACode: rcaForm?.linkedRCACode?.trim(),
      linkedTaskCode: rcaForm?.linkedTaskCode?.trim(),

      // RCA OWNERSHIP & SUPERVISION
      rcaAssistantCode: rcaForm?.rcaAssistantCode?.trim(),
      rcaSupervisorCode: rcaForm?.rcaSupervisorCode?.trim(),

      // RCA DURATION
      rcaFromDate: rcaForm?.rcaFromDate ? new Date(rcaForm.rcaFromDate) : new Date().toISOString().split("T")[0],
      rcaToDate: rcaForm?.rcaToDate ? new Date(rcaForm.rcaToDate) : new Date().toISOString().split("T")[0],
      completionDate: rcaForm?.completionDate ? new Date(rcaForm.completionDate) :new Date().toISOString().split("T")[0],

      // RCA STATUS FLAGS
      // isCompleted: rcaForm?.isCompleted || false,
      isRecurring: rcaForm?.isRecurring || false,
      isPriority: rcaForm?.isPriority || false,
      isFinal: rcaForm?.isFinal || false,
      isActive: rcaForm?.isActive ?? true,
    };

    // API CONFIG
    const config = {
      url: "/hafhrca",
      method: "post",
      data: payload,
      headers: { Authorization: authState.token },
    };

    const res = await fetchData(config, { silent: true });

    // SUCCESS
    if (res?.status) {
      showMessage("RCA saved successfully");
      resetRCAForm();
      fetchRCA();
    }
  } catch (err) {
    console.error(err);
    showMessage("Failed to save RCA", "error");
  } finally {
    setIsSaving(false);
  }
};


const handleUpdateRCA = async () => {
  try {
   const payload = {
      userCode: rcaForm?.userCode || "",
      userRCAID: rcaForm?.userRCAID || "",
      rcaCode: rcaForm?.rcaCode || "",
      rcaGroupCode: rcaForm?.rcaGroupCode || "",
      rcaType: rcaForm?.rcaType || "",
      category: rcaForm?.category?.trim(),
      subCategory: rcaForm?.subCategory?.trim(),
      concernArea: rcaForm?.concernArea?.trim(),
      rcaFor: rcaForm?.rcaFor || "Self",
      rcaBy: rcaForm?.rcaBy || "Human",

      problemStatement: rcaForm?.problemStatement?.trim(),
      why1: rcaForm?.why1?.trim(),
      why2: rcaForm?.why2?.trim(),
      why3: rcaForm?.why3?.trim(),
      why4: rcaForm?.why4?.trim(),
      why5: rcaForm?.why5?.trim(),
      rootCause: rcaForm?.rootCause?.trim(),
      summary: rcaForm?.summary?.trim(),
      remarks: rcaForm?.remarks?.trim(),
      tags: Array.isArray(rcaForm?.tags)
        ? rcaForm.tags
        : String(rcaForm?.tags || "")
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),

      quickSolutionsNote: rcaForm?.quickSolutionsNote?.trim(),
      quickSolutionsSelectionNote: rcaForm?.quickSolutionsSelectionNote?.trim(),
      quickSolutionsActionsNote: rcaForm?.quickSolutionsActionsNote?.trim(),
      quickImpactNotes: rcaForm?.quickImpactNotes?.trim(),

      teamCode: rcaForm?.teamCode?.trim(),
      taskCode: rcaForm?.taskCode?.trim(),
      linkedRCACode: rcaForm?.linkedRCACode?.trim(),
      linkedTaskCode: rcaForm?.linkedTaskCode?.trim(),

      rcaAssistantCode: rcaForm?.rcaAssistantCode?.trim(),
      rcaSupervisorCode: rcaForm?.rcaSupervisorCode?.trim(),

      rcaFromDate: rcaForm?.rcaFromDate ? new Date(rcaForm.rcaFromDate) : new Date().toISOString().split("T")[0],
      rcaToDate: rcaForm?.rcaToDate ? new Date(rcaForm.rcaToDate) : new Date().toISOString().split("T")[0],
      completionDate: rcaForm?.completionDate ? new Date(rcaForm.completionDate) : new Date().toISOString().split("T")[0],

      // isCompleted: rcaForm?.isCompleted || false,
      isRecurring: rcaForm?.isRecurring || false,
      isPriority: rcaForm?.isPriority || false,
      isFinal: rcaForm?.isFinal || false,
      isActive: rcaForm?.isActive ?? true,
    };

    // API CONFIG
    const config = {
      url: `/hafhrca/${editingRCAId}`,
      method: "put",
      data: payload,
      headers: { Authorization: authState.token },
    };

    const res = await fetchData(config, { silent: true });

    // SUCCESS
    if (res?.status) {
      showMessage("RCA updated successfully");
      resetRCAForm();
      setIsEditing(false);
      setEditingRCAId(null);
      fetchRCA();
    }
  } catch (err) {
    console.error(err);
    showMessage("Failed to update RCA", "error");
  }
};

  const handleDeleteRCA = async (id) => {
    try {
      const config = {
        url: `/hafhrca/${id}`,
        method: "delete",
        headers: {
          Authorization: authState.token,
        },
      };
      const res = await fetchData(config,{ silent: true });
      if (res?.status) {showMessage("RCA deleted successfully");
        fetchRCA();
      }
    } catch (err) {
      console.error(err);
      showMessage("Failed to delete RCA", "error");
    }
  };

  const filtered = rcaList.filter((rca) => {
    const dateValue = rca?.rcamDate;
    const dateObj = dateValue ? new Date(dateValue) : null;
    const now = new Date();
    let matchesDate = true;
    if (dateObj) {
      const rcaDay = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      if (dateFilter === "today") {matchesDate = rcaDay.getTime() === today.getTime();}
      // THIS WEEK
      else if (dateFilter === "thisweek") {
        const startOfWeek = new Date(today);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        startOfWeek.setHours(0, 0, 0, 0);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);
        matchesDate = dateObj >= startOfWeek && dateObj <= endOfWeek;
      }
      // THIS MONTH
      else if (dateFilter === "thismonth") {
        matchesDate =
          dateObj.getMonth() === now.getMonth() &&
          dateObj.getFullYear() === now.getFullYear();
      }
      // else if (dateFilter === "completed") { matchesDate = rca?.isCompleted === true;}
      // else if (dateFilter === "pending") {matchesDate = rca?.isCompleted === false;}
      else if (dateFilter === "priority") {matchesDate = rca?.isPriority === true;}
      else if (dateFilter === "recurring") {matchesDate = rca?.isRecurring === true;}
      else if (dateFilter === "final") {matchesDate = rca?.isFinal === true;}
      else if (dateFilter === "active") {matchesDate = rca?.isActive === true;}
    }
    let matchesAction = true;
    if (actionFilter === "self") {matchesAction = rca?.rcaFor === "Self";}
    else if (actionFilter === "student") {matchesAction = rca?.rcaFor === "Student";}
    else if (actionFilter === "parent") {matchesAction = rca?.rcaFor === "Parent";}
    else if (actionFilter === "owner") {matchesAction = rca?.rcaFor === "Owner";}
    else if (actionFilter === "trustee") {matchesAction = rca?.rcaFor === "Trustee";}
    else if (actionFilter === "institution") {matchesAction = rca?.rcaFor === "Institution";}
    else if (actionFilter === "mess") {matchesAction = rca?.rcaFor === "Mess";}
    else if (actionFilter === "transporter") {matchesAction = rca?.rcaFor === "Transporter";}
    else if (actionFilter === "huma") {matchesAction = rca?.rcaFor === "HUMA";}
    return matchesDate && matchesAction;
  });

  // RCA COUNTS
  const selfCount =rcaList.filter((a) => a?.rcaFor === "Self").length;
  const studentCount =rcaList.filter((a) => a?.rcaFor === "Student").length;
  const parentCount = rcaList.filter((a) => a?.rcaFor === "Parent").length;
  const ownerCount =rcaList.filter((a) => a?.rcaFor === "Owner").length;
  const trusteeCount =rcaList.filter((a) => a?.rcaFor === "Trustee").length;
  const institutionCount =rcaList.filter((a) => a?.rcaFor === "Institution").length;
  const messCount =rcaList.filter((a) => a?.rcaFor === "Mess").length;
  const transporterCount =rcaList.filter((a) => a?.rcaFor === "Transporter").length;
  const humaCount =rcaList.filter((a) => a?.rcaFor === "HUMA").length;
  const finalCount =rcaList.filter((a) => a?.isFinal).length;
  const priorityCount =rcaList.filter((a) => a?.isPriority).length;
  const recurringCount =rcaList.filter((a) => a?.isRecurring).length;

  const toggleVoiceInput = () => {
    if (isVoiceListening) {
      stopVoiceInput();
      return;
    }
    startVoiceInput();
  };
  
  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showMessage("Speech Recognition not supported in this browser", "error");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";           
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        setRCADetails(prev => prev + " " + transcript);
      }
    };

    recognition.onerror = (event) => {
      console.error(event);
      if (event.error !== "no-speech") {
        showMessage("Voice recognition error. Please try again.", "error");
        stopVoiceInput();
      }
    };

    recognition.onend = () => {
      setIsVoiceActive(false);
      setIsVoicePaused(false);
    };
    try {
      recognition.start();
      setRecognitionInstance(recognition);
      setIsVoiceActive(true);
      setIsVoicePaused(false);
      // Auto focus on textarea
      document.getElementById("description")?.focus();
      showMessage("🎤 Listening... Speak now", "success");
    } catch (err) {
      showMessage("Failed to start voice input", "error");
    }
  };
  
  const pauseVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoicePaused(true);
    showMessage("Voice input paused", "success");
  };
  
  const resumeVoiceInput = () => {
    setIsVoicePaused(false);
    startVoiceInput();
  };
  
  const stopVoiceInput = () => {
    if (recognitionInstance) recognitionInstance.stop();
    setIsVoiceActive(false);
    setIsVoicePaused(false);
    setRecognitionInstance(null);
    showMessage("Voice input stopped", "success");
  };
  
  const handleMicrophoneClick = () => {
    setShowMicrophoneAlert(true);
  };
  
  const buildRCASpeech = (filtered) => {
    return filtered
      .map((rca, index) => {
        const parts = [];
        parts.push(`RCA ${index + 1}.`);
        if (rca?.rcaFor) parts.push(`For: ${rca.rcaFor}.`);
        if (rca?.category) parts.push(`Category: ${rca.category}.`);
        if (rca?.problemStatement) parts.push(`The problem observed is ${rca.problemStatement}.`);
        if (rca?.why1) parts.push(`This seems to be happening because ${rca.why1}.`);
        if (rca?.why2) parts.push(`Going deeper, another reason appears to be ${rca.why2}.`);
        if (rca?.why3) parts.push(`Further analysis suggests ${rca.why3}.`);
        if (rca?.why4) parts.push(`Another contributing factor is ${rca.why4}.`);
        if (rca?.why5) parts.push(`And finally, one more reason identified is ${rca.why5}.`);
        if (rca?.rootCause) parts.push(`After analysis, the root cause seems to be ${rca.rootCause}.`);
        if (rca?.summary) parts.push(`To summarize, ${rca.summary}.`);
        if (rca?.remarks) parts.push(`Additional remarks: ${rca.remarks}.`);
        // QUICK SOLUTIONS SECTION (only if any exists)
        const quickParts = [];
        if (rca?.quickSolutionsNote) quickParts.push(`Quick Solution: ${rca.quickSolutionsNote}.`);
        if (rca?.quickSolutionsSelectionNote) quickParts.push(`Selected Solution: ${rca.quickSolutionsSelectionNote}.`);
        if (rca?.quickSolutionsActionsNote) quickParts.push(`Action Taken: ${rca.quickSolutionsActionsNote}.`);
        if (rca?.quickImpactNotes) quickParts.push(`Impact: ${rca.quickImpactNotes}.`);
        if (quickParts.length > 0) { parts.push(`Quick Solutions: ${quickParts.join(" ")}`);
        }
        return parts.join(" ");
      })
      .join(" ");
  };

  const buildSingleRCASpeech = (rca) => {
    const parts = [];
    parts.push(`Let me walk you through this RCA.`);
    if (rca?.rcaFor) parts.push(`This RCA is for ${rca.rcaFor}.`);
    if (rca?.category) parts.push(`It belongs to the category of ${rca.category}.`);
    if (rca?.problemStatement) parts.push(`The problem observed is ${rca.problemStatement}.`);
    if (rca?.why1) parts.push(`This seems to be happening because ${rca.why1}.`);
    if (rca?.why2) parts.push(`Going deeper, ${rca.why2}.`);
    if (rca?.why3) parts.push(`Further analysis shows ${rca.why3}.`);
    if (rca?.why4) parts.push(`Another factor is ${rca.why4}.`);
    if (rca?.why5) parts.push(`And one more reason is ${rca.why5}.`);
    if (rca?.rootCause) parts.push(`After analysis, the root cause is ${rca.rootCause}.`);
    if (rca?.summary) parts.push(`To summarize, ${rca.summary}.`);
    if (rca?.remarks) parts.push(`Additional remarks: ${rca.remarks}.`);
    return parts.join(" ");
  };

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: '#fde68a' } },
    },
    scales: {
      x: { ticks: { color: '#d1d5db' }, grid: { color: 'rgba(255,255,255,0.05)' } },
      y: { beginAtZero: true, ticks: { color: '#d1d5db', stepSize: 20 }, grid: { color: 'rgba(255,255,255,0.05)' } },
    },
  }), []);

  // ====================== CHART ======================
  const chartData = useMemo(() => ({
    labels: [t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat')],
    datasets: [
      {
        label: t('rcaCount'),
        data: [65, 72, 70, 80, 82, 88],
        borderColor: '#14b8a6',
        backgroundColor: 'rgba(20,184,166,0.18)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointBackgroundColor: '#fde68a',
      },
    ],
  }), [selectedLanguage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-950 to-slate-900 text-gray-100">
      <div className="mx-auto max-w-7xl space-y-2">
        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-gradient-to-r from-black via-slate-950 to-slate-900 p-5 shadow-2xl sm:p-8">
          <div className="flex flex-col gap-2 justify-start lg:flex-row">
            <div className="flex-1 text-left justify-between">
              <div className="mb-2 flex flex-row gap-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 text-xs lg:text-sm text-gray-200 backdrop-blur-sm">
                  <i className="fa-solid fa-bolt text-yellow-300"></i>
                  {t("deepThinking")}
                </div>
                <div className="flex flex-col gap-2 px-3 py-1">
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className="w-full sm:w-48 rounded-sm border border-slate-700 bg-slate-900 px-3 py-1 text-xs lg:text-sm text-white outline-none transition-all focus:border-teal-500"
                  >
                    {languages.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <h1 className="text-left text-xl lg:text-2xl font-black leading-tight text-yellow-200">{t("rca")}</h1>
              <p className="mt-2 text-left text-xs lg:text-sm leading-7 text-gray-300 lg:text-sm">{t('rcaSub')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="rounded-3xl border border-slate-800 bg-slate-900 p-3 shadow-xl">
          <div className="grid grid-cols-3 gap-2 lg:grid-cols-6">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`flex w-full items-center justify-center rounded-md px-2 py-1 text-center text-xs lg:text-sm font-semibold transition-all lg:text-sm ${
                  activeTab === tab ? 'bg-teal-700 text-yellow-200 shadow-lg shadow-teal-900/30' : 'bg-slate-800 text-gray-200 hover:bg-slate-700 hover:text-yellow-200'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          <div className="space-y-5 xl:col-span-2">
            {/* ====================== RCA LIST ====================== */}
            <div ref={listSectionRef} className="rounded-xl border border-slate-800 bg-slate-700 p-2 shadow-2xl">
              <div className="mb-2 flex flex-col gap-4">
                <div>
                  <h2 className="text-lg lg:text-xl font-black text-yellow-200">{t('rcaList')}</h2>
                  <p className="mt-1 text-sm text-gray-300">{t('rcaListDesc')}</p>
                </div>
                <div className="mb-1 grid grid-cols-2 lg:grid-cols-5 gap-1 border-b border-cyan-300 pb-2">
                  {[
                    { key: "all", label: t("all"), count: rcaList.length },
                    { key: "self", label: t("self"), count: selfCount },
                    { key: "student", label: t("student"), count: studentCount },
                    { key: "parent", label: t("parent"), count: parentCount },
                    { key: "owner", label: t("owner"), count: ownerCount },
                    { key: "trustee", label: t("trustee"), count: trusteeCount },
                    { key: "institution", label: t("institution"), count: institutionCount },
                    { key: "mess", label: t("mess"), count: messCount },
                    { key: "transporter", label: t("transporter"), count: transporterCount },
                    { key: "huma", label: t("huma"), count: humaCount },
                    { key: "completed", label: t("completed"), count: finalCount },
                    { key: "priority", label: t("priority"), count: priorityCount },
                    { key: "recurring", label: t("recurring"), count: recurringCount },
                  ].map((af) => (
                    <button
                      key={af.key}
                      onClick={() => setActionFilter(af.key)}
                      className={` rounded-md px-2 py-1 text-xs lg:text-sm transition-all flex
                        items-center justify-between gap-2
                        ${
                          actionFilter === af.key
                            ? "bg-teal-600 text-white"
                            : "bg-slate-800 text-gray-300 hover:bg-slate-700"
                        }
                      `}
                    >
                      <span>{af.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full
                          ${
                            actionFilter === af.key
                              ? "bg-white/20 text-white"
                              : "bg-slate-700 text-cyan-300"
                          }
                        `}
                      >
                        {af.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-1 grid grid-cols-3 lg:grid-cols-5 gap-1 pb-2">
                {["all", "today", "thisweek", "thismonth", "completed", "pending", "priority", "recurring", "final", "active"].map((f) => (
                  <button key={f} onClick={() => setDateFilter(f)} className={`rounded-md px-2 py-1 text-xs lg:text-sm transition-all ${dateFilter === f ? "bg-teal-600 text-white" : "bg-slate-800 text-gray-300 hover:bg-slate-700"}`}>
                    {t(f)}
                  </button>
                ))}
                <button
                  onClick={() => {
                    setActiveTab(t("create"));
                    setTimeout(() => createSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
                  }}
                  className="col-span-3 lg:col-span-2 text-xs lg:text-sm rounded-md bg-blue-700 px-2 py-2 font-bold text-white shadow-lg transition-all hover:bg-blue-600"
                >
                  <i className="fa-solid fa-plus mr-2"></i>{t("addRCA")}
                </button>
              </div>
              <div className='flex justify-end'>
                {!isListSpeaking ? (
                  <button
                    onClick={() => {
                      setIsListSpeaking(true);
                      const speechText = filtered
                        .map((rca, index) => {
                          return `
                            RCA ${index + 1}.
                            For: ${rca.rcaFor || "Not specified"}.
                            Category: ${rca.category || "Not specified"}.
                            Problem: ${rca.problemStatement || "Not specified"}.
                          `;
                        })
                        .join(" ");

                      speakText(speechText);
                    }}
                    className="rounded-md bg-yellow-600 px-2 py-1 text-xs lg:text-sm w-fit font-bold text-black hover:bg-yellow-500"
                  >
                    <i className="fa-solid fa-volume-high mr-2"></i>
                    {t("listenList")}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { speechSynthesis.pause(); setIsPaused(true);}}
                      className="rounded-xl bg-blue-600 px-2 py-1 text-xs lg:text-sm w-fit font-bold text-white"
                    >
                      <i className="fa-solid fa-pause mr-1"></i> Pause
                    </button>
                    <button
                      onClick={() => {speechSynthesis.resume();setIsPaused(false);}}
                      className="rounded-xl bg-yellow-600 px-2 py-1 text-xs lg:text-sm w-fit font-bold text-black"
                    >
                      <i className="fa-solid fa-play mr-1"></i> Resume
                    </button>

                    <button
                      onClick={() => {
                        speechSynthesis.cancel();
                        setIsListSpeaking(false);
                        setIsPaused(false);
                        setActiveTtsId(null);
                      }}
                      className="rounded-xl bg-red-600 px-3 py-1 text-xs lg:text-sm w-fit font-bold text-white"
                    >
                      <i className="fa-solid fa-stop mr-1"></i> Stop
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {filtered.map((rca) => (
                  <div key={rca._id} className="rounded-3xl border border-slate-800 bg-slate-800 p-5 transition-all hover:border-teal-500 hover:shadow-lg">
                    <div className="flex flex-col gap-4">
                      <div className="flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs lg:text-sm text-teal-300">
                          <span>
                            <i className="fa-regular fa-calendar mr-1"></i>
                            {rca?.rcaDate
                              ? new Date(rca.rcaDate).toLocaleDateString("en-IN")
                              : "No Date"}
                          </span>

                          <span>Causal Analysis From: </span>
                          <span>
                            <i className="fa-regular fa-calendar mr-1"></i>
                            {rca?.rcaFromDate
                              ? new Date(rca.rcaFromDate).toLocaleDateString("en-IN")
                              : "No Date"}
                          </span>
                          <span>To: </span>
                          <span>
                            <i className="fa-regular fa-calendar mr-1"></i>
                            {rca?.rcaToDate
                              ? new Date(rca.rcaToDate).toLocaleDateString("en-IN")
                              : "No Date"}
                          </span>

                          <span className="rounded-full bg-sky-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-sky-300">
                            {rca?.rcaType || "General"}
                          </span>

                          <span className="rounded-full bg-purple-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-purple-300">
                            {rca?.rcaFor}
                          </span>

                          {rca?.isPriority && (
                            <span className="rounded-full bg-red-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-red-300">
                              <i className="fa-solid fa-triangle-exclamation mr-1"></i>
                              {t("priority")}
                            </span>
                          )}

                          {rca?.isRecurring && (
                            <span className="rounded-full bg-orange-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-orange-300">
                              <i className="fa-solid fa-rotate mr-1"></i>
                              {t("recurring")}
                            </span>
                          )}

                          {rca?.isFinal && (
                            <span className="rounded-full bg-emerald-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-emerald-300">
                              <i className="fa-solid fa-circle-check mr-1"></i>
                              {t("completed")}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-xs lg:text-sm font-bold text-yellow-200">
                            {rca?.concernArea}
                          </h3>

                          {rca?.tts && (
                            <span className="rounded-full bg-orange-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-orange-300">
                              <i className="fa-solid fa-volume-high mr-1"></i>
                              {t("ttsEnabled")}
                            </span>
                          )}

                          {rca?.category && (
                            <span className="rounded-full bg-cyan-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-cyan-300">
                              {rca.category}
                            </span>
                          )}

                          {rca?.subCategory && (
                            <span className="rounded-full bg-indigo-700/20 px-3 py-1 text-xs lg:text-sm font-semibold text-indigo-300">
                              {rca.subCategory}
                            </span>
                          )}
                        </div>

                        <div
                          onClick={() => setExpandedRCA(prev => prev === rca._id ? null : rca._id)}
                          className="mt-3 w-full min-w-0 flex-1 block cursor-pointer 
                                rounded-2xl border border-slate-700 bg-slate-900/40 p-3 transition-all
                                hover:border-teal-500 hover:bg-slate-900"
                        >
                          <div className="mb-2 flex items-center justify-between">
                            <div className="text-xs lg:text-sm font-semibold uppercase tracking-wide text-cyan-300">
                              {t("problemStatement")}
                            </div>
                            <button className="w-10 text-xs lg:text-sm text-yellow-300">
                              <i className={`fa-solid ${expandedRCA === rca._id ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                            </button>
                          </div>
                          <p className="text-sm leading-6 text-gray-300">
                            {rca?.problemStatement}
                          </p>
                          {expandedRCA === rca._id && (
                            <div className="mt-3 space-y-3">
                              {(rca?.why1 || rca?.why2 || rca?.why3 || rca?.why4 || rca?.why5) && (
                                <div className="rounded-2xl border border-yellow-700/30 bg-yellow-900/10 p-3">
                                  <div className="mb-3 text-xs lg:text-sm font-semibold uppercase tracking-wide text-yellow-300">
                                    {t("fiveWhyAnalysis")}
                                  </div>

                                  <div className="space-y-2">

                                    {rca?.why1 && (
                                      <div className="rounded-xl bg-slate-900/50 p-2">
                                        <div className="text-xs lg:text-sm font-bold text-yellow-200">{t("why1")}</div>
                                        <p className="text-sm text-gray-200">{rca.why1}</p>
                                      </div>
                                    )}

                                    {rca?.why2 && (
                                      <div className="rounded-xl bg-slate-900/50 p-2">
                                        <div className="text-xs lg:text-sm font-bold text-yellow-200">{t("why2")}</div>
                                        <p className="text-sm text-gray-200">{rca.why2}</p>
                                      </div>
                                    )}

                                    {rca?.why3 && (
                                      <div className="rounded-xl bg-slate-900/50 p-2">
                                        <div className="text-xs lg:text-sm font-bold text-yellow-200">{t("why3")}</div>
                                        <p className="text-sm text-gray-200">{rca.why3}</p>
                                      </div>
                                    )}

                                    {rca?.why4 && (
                                      <div className="rounded-xl bg-slate-900/50 p-2">
                                        <div className="text-xs lg:text-sm font-bold text-yellow-200">{t("why4")}</div>
                                        <p className="text-sm text-gray-200">{rca.why4}</p>
                                      </div>
                                    )}

                                    {rca?.why5 && (
                                      <div className="rounded-xl bg-slate-900/50 p-2">
                                        <div className="text-xs lg:text-sm font-bold text-yellow-200">{t("why5")}</div>
                                        <p className="text-sm text-gray-200">{rca.why5}</p>
                                      </div>
                                    )}

                                  </div>
                                </div>
                              )}

                              {rca?.rootCause && (
                                <div className="rounded-2xl border border-red-700/30 bg-red-900/10 p-3">
                                  <div className="mb-1 text-xs lg:text-sm font-semibold uppercase tracking-wide text-red-300">
                                    {t("rootCause")}
                                  </div>

                                  <p className="text-sm text-red-100">
                                    {rca.rootCause}
                                  </p>
                                </div>
                              )}

                              {rca?.summary && (
                                <div className="rounded-2xl border border-emerald-700/30 bg-emerald-900/10 p-3">
                                  <div className="mb-1 text-xs lg:text-sm font-semibold uppercase tracking-wide text-emerald-300">
                                    {t("summary")}
                                  </div>

                                  <p className="text-sm text-emerald-100">
                                    {rca.summary}
                                  </p>
                                </div>
                              )}

                              {(rca?.quickSolutionsNote || rca?.quickSolutionsSelectionNote || rca?.quickSolutionsActionsNote || rca?.quickImpactNotes) && (
                                <div className="rounded-2xl border border-cyan-700/30 bg-cyan-900/10 p-3">
                                  <div className="mb-3 text-xs lg:text-sm font-semibold uppercase tracking-wide text-cyan-300">
                                    {t("quickSolutions")}
                                  </div>
                                  <div className="space-y-3">
                                    {rca?.quickSolutionsNote && (
                                      <div>
                                        <div className="text-xs lg:text-sm font-bold text-cyan-200">
                                          {t("quickSolutionsNote")}
                                        </div>

                                        <p className="text-sm text-gray-200">
                                          {rca.quickSolutionsNote}
                                        </p>
                                      </div>
                                    )}
                                    {rca?.quickSolutionsSelectionNote && (
                                      <div>
                                        <div className="text-xs lg:text-sm font-bold text-cyan-200">
                                          {t("quickSolutionsSelectionNote")}
                                        </div>

                                        <p className="text-sm text-gray-200">
                                          {rca.quickSolutionsSelectionNote}
                                        </p>
                                      </div>
                                    )}
                                    {rca?.quickSolutionsActionsNote && (
                                      <div>
                                        <div className="text-xs lg:text-sm font-bold text-cyan-200">
                                          {t("quickSolutionsActionsNote")}
                                        </div>

                                        <p className="text-sm text-gray-200">
                                          {rca.quickSolutionsActionsNote}
                                        </p>
                                      </div>
                                    )}
                                    {rca?.quickImpactNotes && (
                                      <div>
                                        <div className="text-xs lg:text-sm font-bold text-cyan-200">
                                          {t("quickImpactNotes")}
                                        </div>

                                        <p className="text-sm text-gray-200">
                                          {rca.quickImpactNotes}
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          {rca?.tags?.map((tag, i) => (
                            <span key={i} className="rounded-full bg-slate-900 px-3 py-1 text-xs lg:text-sm font-medium text-gray-100">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex gap-2 bg-gray-800 border border-gray-600">
                        <div className={`flex gap-2 ${isListSpeaking ? "opacity-40 pointer-events-none" : ""}`}>
                          {activeTtsId !== rca._id ? (
                            <button onClick={() => {
                                setIsListSpeaking(true);
                                setActiveTtsId(rca._id);
                                const speechText = buildSingleRCASpeech(rca);
                                speakText(speechText);
                              }} 
                              className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : isPaused ? (
                            <button onClick={resumeSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-yellow-700 px-1 py-1 text-black hover:bg-yellow-600">
                              <i className="fa-solid fa-play"></i>
                            </button>
                          ) : (
                            <>
                              <button onClick={pauseSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-blue-700 px-1 py-1 text-white hover:bg-blue-600">
                                <i className="fa-solid fa-pause"></i>
                              </button>
                              <button onClick={stopSpeech} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-red-700 px-1 py-1 text-white hover:bg-red-600">
                                <i className="fa-solid fa-stop"></i>
                              </button>
                            </>
                          )}
                        </div>

                        <button onClick={() => handleEditRCA(rca)} className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700">
                          <i className="fa-solid fa-pen"></i>
                        </button>
                        <button
                          onClick={() => setActiveAssistantRCAId(prev => prev === rca._id ? null : rca._id)}
                          className="w-10 lg:w-12 text-xs lg:text-sm rounded-xl bg-slate-800 px-1 py-1 text-yellow-200 hover:bg-teal-700"
                        >
                          <i className="fa-solid fa-robot"></i>
                        </button>
                      </div>
                    </div>

                    {activeAssistantRCAId === rca._id && (
                      <div className="mt-2">
                        <AILightComponent
                          param1="hostel_rca_improvement_assistant"
                          param2={`
                            Concern Area: ${rca?.concernArea}
                            Problem Statement: ${rca?.problemStatement}
                            Why 1: ${rca?.why1}
                            Why 2: ${rca?.why2}
                            Why 3: ${rca?.why3}
                            Why 4: ${rca?.why4}
                            Why 5: ${rca?.why5}
                            Root Cause: ${rca?.rootCause}
                            Summary: ${rca?.summary}
                            Remarks: ${rca?.remarks}
                            Suggested Solution: ${rca?.quickSolutionsNote}
                            Impact Notes: ${rca?.quickImpactNotes}
                          `}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ====================== CREATE / EDIT FORM ====================== */}
            <div ref={createSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-3 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-square-plus text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-lg lg:text-xl font-black text-yellow-200">
                    {isEditing ? t("editRCA") : t("createRCA")}
                  </h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">
                    {isEditing ? t("updateSelectedRCA") : t("createRCADesc")}
                  </p>
                </div>
              </div>

              {/* <div className="space-y-5"> */}
              <div className="rounded-2xl border border-cyan-800/40 bg-slate-950/40 p-4 space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-700 text-white">
                    <i className="fa-solid fa-link"></i>
                  </div>
                  <div>
                    <div className="text-xs lg:text-sm font-bold tracking-wide text-cyan-200">
                      {t("rcaCode")}
                    </div>
                    <div className="text-[10px] lg:text-xs text-gray-400">
                      Intelligent RCA or Causal Chain Identifier
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-cyan-500/20 bg-slate-950/70 px-4 py-3">
                  <div className="text-lg lg:text-2xl font-extrabold tracking-widest text-yellow-300 break-all">
                    {rcaCode}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-xs lg:text-sm text-gray-300 leading-relaxed">
                  <p className="text-orange-300 text-xs lg:text-sm">
                    This is an important reference code that enables Think5Whys to create, connect, identify, and analyze linked chains of issues, problems, goals, objectives, actions, dependencies, and workflows over time.
                  </p>
                  <p className="text-cyan-300 text-xs lg:text-sm">
                    Even without AI assistance, this code supports structured tracking, historical mapping, operational visibility, accountability, and relationship discovery across connected RCAs.
                  </p>
                  <p className="text-orange-300 text-xs lg:text-sm">
                    With AI-powered analysis, these connected RCA chains can generate deeper insights into problem statements and 5 Whys analysis, including root causes and solution recommendations through actionable causal analysis.
                  </p>
                </div>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                  {t("rcaDate")}
                </label>
                <input
                  type="date"
                  value={rcaForm.rcaDate || ""}
                  onChange={(e) => setRCAForm(prev => ({ ...prev, rcaDate: e.target.value }))}
                  className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs lg:text-sm font-bold text-yellow-200">
                  {t("problemStatement")}*
                </label>
                <textarea
                  value={rcaForm.problemStatement || ""}
                  onChange={(e) => setRCAForm(prev => ({ ...prev, problemStatement: e.target.value }))}
                  placeholder="Describe the issue in detail..."
                  maxLength="1000"
                  style = {{height: "200px"}}
                  className="w-full rounded-2xl border border-slate-700 bg-black px-4 py-3 text-gray-100 outline-none focus:border-teal-500 min-h-[140px]"
                />
              </div>

              <div className='mt-2 mb-2'>
                <button
                  onClick={() => setShowInputWhys((prev) => !prev)}
                  className="rounded-xl bg-teal-700 px-3 py-2 text-xs lg:text-sm text-white hover:bg-teal-600"
                >
                  {showInputWhys ? "Hide Whys & RCA Input" : "Show Whys & RCA Input"}
                </button>
              </div>
              { showInputWhys && (
                <>
                <div className="flex flex-col space-y-1 mt-4">
                  {[
                    { key: "why1", label: "Why 1" },
                    { key: "why2", label: "Why 2" },
                    { key: "why3", label: "Why 3" },
                    { key: "why4", label: "Why 4" },
                    { key: "why5", label: "Why 5" },
                  ].map((item) => (
                    <>
                      <label className="text-xs lg:text-sm">{item.label}</label>
                      <input
                        key={item.key}
                        value={rcaForm[item.key] || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, [item.key]: e.target.value }))}
                        placeholder={item.label}
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100"
                      />
                    </>
                  ))}
                </div>

                <div className="flex flex-col space-y-1 mt-2"> 
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("rootCause")}
                  </label>
                  <textarea
                    value={rcaForm.rootCause || ""}
                    onChange={(e) => setRCAForm(prev => ({ ...prev, rootCause: e.target.value }))}
                    placeholder="Identified root cause..."
                    maxLength="1000"
                    style = {{height: "200px"}}
                    className="w-full min-h-[120px] rounded-2xl border border-red-800 bg-slate-950 px-4 py-3 text-gray-100"
                  />
                </div>
                </>
              )}

              <div className='mt-2 mb-2'>
                <button
                  onClick={() => setShowMoreInputs((prev) => !prev)}
                  className="rounded-xl bg-teal-700 px-3 py-2 text-xs lg:text-sm text-white hover:bg-teal-600"
                >
                  {showMoreInputs ? "Hide More Inputs" : "Show More Inputs"}
                </button>
              </div>
              { showMoreInputs && (
                <>
                <div className='flex flex-col gap-2'>
                  <div className="flex flex-col space-y-1"> 
                    <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">
                      {t("concernArea")}
                    </label>
                    <input
                      type="text"
                      ref={rcaTitleRef}
                      value={rcaForm.concernArea || ""}
                      onChange={(e) => setRCAForm(prev => ({ ...prev, concernArea: e.target.value }))}
                      placeholder="Students complaining about mess food quality"
                      className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="flex flex-col space-y-1">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("rcaType")}
                      </label>
                      <select
                        value={rcaForm.rcaType || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, rcaType: e.target.value }))}
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100"
                      >
                        <option value="Operational">Operational</option>
                        <option value="Behavioral">Behavioral</option>
                        <option value="Academic">Academic</option>
                        <option value="Infrastructure">Infrastructure</option>
                        <option value="Management">Management</option>
                        <option value="Personal">Personal</option>
                      </select>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("rcaFor")}
                      </label>
                      <select
                        value={rcaForm.rcaFor || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, rcaFor: e.target.value }))}
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-1 text-gray-100"
                      >
                        <option value="Self">Self</option>
                        <option value="Student">Student</option>
                        <option value="Parent">Parent</option>
                        <option value="Owner">Owner</option>
                        <option value="Trustee">Trustee</option>
                        <option value="Institution">Institution</option>
                        <option value="Mess">Mess</option>
                        <option value="Transporter">Transporter</option>
                        <option value="HUMA">HUMA</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="flex flex-col space-y-1">
                      <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">
                        {t("category")}*
                      </label>
                      <input
                        type="text"
                        value={rcaForm.category || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, category: e.target.value }))}
                        placeholder="Hostel Management"
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                    <div className="flex flex-col space-y-1">
                      <label className="mb-2 text-xs lg:text-sm font-bold text-yellow-200">
                        {t("subCategory")}
                      </label>
                      <input
                        type="text"
                        value={rcaForm.subCategory || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, subCategory: e.target.value }))}
                        placeholder="Food Quality"
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <div className="flex flex-col space-y-1">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("rcaFrom")}
                      </label>
                      <input
                        type="date"
                        value={rcaForm.rcaFromDate || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, rcaFromDate: e.target.value }))}
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                      />
                    </div>
                    <div className="flex flex-col space-y-1">  
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("rcaTo")}
                      </label>
                      <input
                        type="date"
                        value={rcaForm.rcaToDate || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, rcaToDate: e.target.value }))}
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500 focus:ring-4 focus:ring-teal-900/40"
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-2'> 
                    <div className="flex flex-wrap gap-3">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("linkedRCACode")}
                      </label>
                      <input
                        type="text"
                        value={rcaForm.linkedRCACode || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, linkedRCACode: e.target.value }))}
                        placeholder="Previous RCA Code..."
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("linkedTaskCode")}
                      </label>
                      <input
                        type="text"
                        value={rcaForm.linkedTaskCode || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, linkedTaskCode: e.target.value }))}
                        placeholder="Related Task Code..."
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-2'> 
                    <div className="flex flex-wrap gap-3">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("assistantCode")}
                      </label>
                      <input
                        type="text"
                        value={rcaForm.rcaAssistantCode || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, rcaAssistantCode: e.target.value }))}
                        placeholder="Previous RCA Code..."
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("supervisorCode")}
                      </label>
                      <input
                        type="text"
                        value={rcaForm.rcaSupervisorCode || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, rcaSupervisorCode: e.target.value }))}
                        placeholder="Related Task Code..."
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                    <div className="flex flex-wrap gap-3">
                      <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                        {t("teamCode")}
                      </label>
                      <input
                        type="text"
                        value={rcaForm.teamCode || ""}
                        onChange={(e) => setRCAForm(prev => ({ ...prev, teamCode: e.target.value }))}
                        placeholder="Team Code..."
                        className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100 outline-none focus:border-teal-500"
                      />
                    </div>
                  </div>
                </div>
                </>
              )}

              <div className='mt-2 mb-2'>
                <button
                  onClick={() => setShowQuickSolutions((prev) => !prev)}
                  className="rounded-xl bg-teal-700 px-3 py-2 text-xs lg:text-sm text-white hover:bg-teal-600"
                >
                  {showQuickSolutions ? "Hide Quick Solutions" : "Show Quick Solutions"}
                </button>
              </div>
              { showQuickSolutions && (
                <>
                <div className="flex flex-wrap gap-3">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("quickSolutions")}
                  </label>
                  <textarea
                    value={rcaForm.quickSolutionsNote || ""}
                    onChange={(e) => setRCAForm(prev => ({ ...prev, quickSolutionsNote: e.target.value }))}
                    placeholder="Possible quick solutions..."
                    maxLength="1000"
                    style = {{height: "200px"}}
                    className="text-xs lg:text-sm w-full min-h-[120px] rounded-2xl border border-emerald-800 bg-slate-950 px-4 py-3 text-gray-100"
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("quickSolutionSelected")}
                  </label>
                  <textarea
                    value={rcaForm.quickSolutionsSelectionNote || ""}
                    onChange={(e) => setRCAForm(prev => ({ ...prev, quickSolutionsSelectionNote: e.target.value }))}
                    placeholder="Selected from Quick solutions..."
                    maxLength="1000"
                    style = {{height: "200px"}}
                    className="text-xs lg:text-sm w-full min-h-[120px] rounded-2xl border border-emerald-800 bg-slate-950 px-4 py-3 text-gray-100"
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("quickSolutionAction")}
                  </label>
                  <textarea
                    value={rcaForm.quickSolutionsActionsNote || ""}
                    onChange={(e) => setRCAForm(prev => ({ ...prev, quickSolutionsActionsNote: e.target.value }))}
                    placeholder="Action Taken on selected solution..."
                    maxLength="1000"
                    style = {{height: "200px"}}
                    className="text-xs lg:text-sm w-full min-h-[120px] rounded-2xl border border-emerald-800 bg-slate-950 px-4 py-3 text-gray-100"
                  />
                </div>
                <div className="flex flex-wrap gap-3">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("quickImpacts")}
                  </label>
                  <textarea
                    value={rcaForm.quickImpactNotes || ""}
                    onChange={(e) => setRCAForm(prev => ({ ...prev, quickImpactNotes: e.target.value }))}
                    placeholder="Impact Note..."
                    maxLength="1000"
                    style = {{height: "200px"}}
                    className="text-xs lg:text-sm w-full min-h-[120px] rounded-2xl border border-emerald-800 bg-slate-950 px-4 py-3 text-gray-100"
                  />
                </div>              

                <div className="flex flex-wrap gap-3">
                  <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                    {t("summary")}
                  </label>
                  <textarea
                    value={rcaForm.summary || ""}
                    onChange={(e) => setRCAForm(prev => ({ ...prev, summary: e.target.value }))}
                    placeholder="Short RCA summary..."
                    maxLength="1000"
                    style = {{height: "200px"}}                      
                    className="text-xs lg:text-sm w-full min-h-[120px] rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100"
                  />
                </div>
                </>
              )}

              <div className="flex flex-wrap gap-3">
                <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                  {t("remarks")}
                </label>
                <textarea
                  value={rcaForm.remarks || ""}
                  onChange={(e) => setRCAForm(prev => ({ ...prev, remarks: e.target.value }))}
                  placeholder="Additional remarks..."
                  maxLength="1000"
                  style = {{height: "200px"}}                      
                  className="text-xs lg:text-sm w-full min-h-[120px] rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100"
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <label className="mb-2 block text-xs lg:text-sm font-bold text-yellow-200">
                  {t("tags")}
                </label>
                <input
                  value={(rcaForm.tags || []).join(", ")}
                  onChange={(e) => setRCAForm(prev => ({
                    ...prev,
                    tags: e.target.value.split(",").map(t => t.trim()).filter(Boolean),
                  }))}
                  placeholder="discipline, food, hostel"
                  className="text-xs lg:text-sm w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-gray-100"
                />
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { key: "isPriority", label: "Priority" },
                  { key: "isRecurring", label: "Recurring" },
                  { key: "isFinal", label: "Final" },
                  { key: "isActive", label: "Active" },
                ].map((item) => (
                    <label key={item.key} className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      checked={rcaForm[item.key] || false}
                      onChange={(e) => setRCAForm(prev => ({ ...prev, [item.key]: e.target.checked }))}
                      className='text-xs lg:text-sm'
                    />
                    {item.label}
                  </label>
                ))}
              </div> 

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setActiveAssistantCreateRCA(!activeAssistantCreateRCA)}
                  className="rounded-2xl bg-teal-700 px-5 py-3 font-bold text-white shadow-lg transition-all hover:bg-teal-600 w-48 text-xs lg:text-sm"
                >
                  <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
                  {t("refineWithAI")}
                </button>
              </div>

              {activeAssistantCreateRCA && (
                <div className="mt-2">
                  <AILightComponent
                    param1="hostel_rca_input_assistant"
                    param2={`
                      Concern Area: ${rcaForm.concernArea}
                      Problem Statement: ${rcaForm.problemStatement}
                      Why 1: ${rcaForm.why1}
                      Why 2: ${rcaForm.why2}
                      Why 3: ${rcaForm.why3}
                      Why 4: ${rcaForm.why4}
                      Why 5: ${rcaForm.why5}
                      Root Cause: ${rcaForm.rootCause}
                      Summary: ${rcaForm.summary}
                      Remarks: ${rcaForm.remarks}
                      Suggested Solution: ${rcaForm.quickSolutionsNote}
                      Impact Notes: ${rcaForm.quickImpactNotes}
                    `}
                  />
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-2">
                <button
                  disabled={isEditing || isSaving || isSaved}
                  onClick={handleSaveRCA}
                  className={`text-xs lg:text-sm rounded-xl px-3 py-2 font-bold text-white shadow-lg transition-all ${(isEditing || isSaving) ? "cursor-not-allowed bg-slate-700 opacity-50" : "bg-teal-600 hover:bg-teal-500"}`}
                >
                  <i className="fa-solid fa-floppy-disk mr-2"></i>
                  {isSaving ? "Saving..." : "Save RCA"}
                </button>

                {isEditing && (
                  <>
                    <button onClick={handleUpdateRCA} className="text-xs lg:text-sm rounded-xl bg-yellow-600 px-3 py-2 font-bold text-black shadow-lg hover:bg-yellow-500">
                      <i className="fa-solid fa-rotate mr-2"></i>
                      Update
                    </button>
                    <button onClick={() => handleDeleteRCA(editingRCAId)} disabled={isDeleting} className="text-xs lg:text-sm rounded-xl bg-red-700 px-3 py-2 font-bold text-white shadow-lg hover:bg-red-600">
                      <i className="fa-solid fa-trash mr-2"></i>
                      Delete
                    </button>
                  </>
                )}

                <button onClick={handleCancelRCA} className="text-xs lg:text-sm rounded-xl bg-slate-700 px-3 py-2 font-bold text-white shadow-lg hover:bg-slate-600">
                  <i className="fa-solid fa-xmark mr-2"></i>
                  Cancel
                </button>
              </div>

              {message.text && (
                <div className={`mt-4 rounded-2xl border px-6 py-4 text-xs lg:text-sm font-medium flex items-center gap-3 shadow-lg ${message.type === "success" ? "bg-green-900/50 border-green-500 text-green-200" : "bg-red-900/50 border-red-500 text-red-200"}`}>
                  <i className={`fa-solid ${message.type === "success" ? "fa-check-circle" : "fa-exclamation-triangle"}`}></i>
                  <span>{message.text}</span>
                  <button onClick={() => setMessage({ type: "", text: "" })} className="w-12 ml-auto text-lg p-2 bg-blue-600 text-white leading-none hover:text-white">
                    ×
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-5">
            <div ref={reportsSectionRef} className="rounded-3xl border border-slate-800 bg-slate-900 p-5 shadow-2xl sm:p-6">
              <div className="mb-2 flex items-start gap-4">
                <div className="rounded-2xl bg-slate-800 p-4 text-yellow-200">
                  <i className="fa-solid fa-chart-column text-lg lg:text-xl"></i>
                </div>
                <div>
                  <h2 className="text-sm lg:text-lg font-black text-yellow-200">{t('rcaTrend')}</h2>
                  <p className="mt-1 text-xs lg:text-sm text-gray-300">{t('monthlyAnalytics')}</p>
                </div>
              </div>
              <div className="h-72">
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};